import * as React from 'react';
import classnames from 'classnames';
import { IProductsDimensionsAvailabilities, FullProduct } from '@msdyn365-commerce/commerce-entities';
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { AttributeValue, CartLine, CategoryPath, CommerceList, Customer, ProductList, ProductPrice, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    IProductInventoryInformation,
    OrderTemplate,
    QueryResultSettingsProxy,
    GetOrderTemplatesInput,
    getOrderTemplates,
    getFallbackImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ICartlinesViewProps } from '@msdyn365-commerce-modules/cart';
import { ProductListRow } from './product-list-row';
import { IWishlistItemsProps } from '../../themes/dlvry-adventureworks/definition-extensions/wishlist-items.ext.props.autogenerated';
import { IWishlistItemsData } from '@msdyn365-commerce-modules/wishlist';
import { useEffect, useMemo, useState } from 'react';
import { IOrderTemplateViewProps } from '../../modules/dlvry-order-template/dlvry-order-template';
import PQueue from 'p-queue';
import { ValidateProductAccessResult } from '../../shared-utils/validate-product-access';
import { getLocale } from '../../shared-utils/attributes/locale';
import { ProductError, SimpleProductExt } from '../../shared-utils/types';
import { ProductSearchResultExt } from '../../modules/dlvry-search-result-container/components';
import { CountingListLine } from '../../actions/entities/counting-list';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { ICartViewProps } from '../../modules/dlvry-cart/dlvry-cart';
import { getTemperatureZone } from '../../shared-utils/attributes/get-temperature-zone';

// Modules where the ProductListComponent is used:
// - Cart
// - Search Result Container
// - Wishlist
// - Order Template

export class ProductListProduct {
    public RecordId?: number;
    public ItemId?: string;
    public Name?: string;
    public PrimaryImageUrl?: string;
    public DefaultUnitOfMeasure?: string;
    public Price?: number;
    public BasePrice?: number;
    public InventoryLabel?: string | undefined;
    public CurrentQuantity?: number | undefined;
    public CartLine?: CartLine | undefined;
    public DiscountAmount?: number | undefined;
    public LineAmount?: number | undefined;
    public Availability?: IProductInventoryInformation;
    public Brand?: string | undefined;
    public Manufacturer?: string | undefined;
    public PriceFailed?: boolean | undefined;
    public LineCreatedDateTime?: Date | undefined;
    public DisplayOrder?: number;
    public CategoryPaths?: CategoryPath[] | undefined;
    public Badge?: string | undefined;
    public ProductPrice?: ProductPrice | undefined;
    public RequestedDeliveryDate?: Date | undefined;
    public Transit?: boolean | undefined;
    public TemperatureZone?: string | undefined;

    public constructor(
        recordId?: number,
        itemId?: string,
        name?: string,
        primaryImageUrl?: string,
        defaultUnitOfMeasure?: string,
        price?: number,
        inventoryLabel?: string | undefined,
        currentQuantity?: number | undefined,
        cartLine?: CartLine | undefined,
        DiscountAmount?: number | undefined,
        LineAmount?: number | undefined,
        availability?: IProductInventoryInformation,
        brand?: string | undefined,
        manufacturer?: string | undefined,
        priceFailed?: boolean | undefined,
        displayOrder?: number | undefined,
        categoryPaths?: CategoryPath[] | undefined,
        badge?: string | undefined,
        productPrice?: ProductPrice | undefined,
        transit?: boolean | undefined,
        temperatureZone?: string | undefined
    ) {
        this.RecordId = recordId;
        this.ItemId = itemId;
        this.Name = name;
        this.PrimaryImageUrl = primaryImageUrl;
        this.DefaultUnitOfMeasure = defaultUnitOfMeasure;
        this.Price = price;
        this.InventoryLabel = inventoryLabel;
        this.CurrentQuantity = currentQuantity;
        this.CartLine = cartLine;
        this.DiscountAmount = DiscountAmount;
        this.LineAmount = LineAmount;
        this.Availability = availability;
        this.Brand = brand;
        this.Manufacturer = manufacturer;
        this.PriceFailed = priceFailed;
        this.DisplayOrder = displayOrder || 0;
        this.CategoryPaths = categoryPaths;
        this.Badge = badge;
        this.ProductPrice = productPrice;
        this.Transit = transit;
        this.TemperatureZone = temperatureZone;
    }
}

export interface IProductListResources {
    productListItemText: string;
    productListItemNumberText: string;
    productListItemNameText: string;
    productListUnitNameText: string;
    productListPriceText: string;
    productListOrderQuantityLabel: string;
    productListTotalPriceText: string;
    productListActionsLabel: string;
    productListRemoveLabel?: string | undefined;
    buyNowButtonText: string;
    discount: string;
    lineAmount: string;
    emptyCartButtonText?: string;
    removeSelectedToCartText?: string;
    removeButtonText?: string;
    priceErrorText?: string;
    inCartTooltipText?: string;
    deliveryDateLabel?: string;
    transitTooltip?: string;
    addToWishlistText?: string;
    removeFromWishlistText?: string;
}

export interface IActionsAvailable {
    addToCartAction?: boolean;
    addToOrderTemplate?: boolean;
    addToWishlistAction?: boolean;
    removeAction?: boolean;
    productComparisonButton?: boolean;
}

interface IProductListItems {
    type?: 'default' | 'cart';
    cart?: ICartState;
    products: ProductListProduct[];
    simpleProducts: SimpleProduct[];
    unitConversions?: UnitOfMeasureConversion[];
    productErrors?: ProductError[];
    context: ICoreContext;
    config?: { [x: string]: any };
    resources: IProductListResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    allowMultiSelect?: boolean;
    telemetryContent: ITelemetryContent;
    orderTemplate?: OrderTemplate;
    customerInformation?: Customer;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isLoading?: boolean;
    isPriceMinMaxEnabled?: boolean;
    disableBuyButtons?: boolean;
    disableReorder?: boolean;
    disableQuantityInput?: boolean;
    draggable?: boolean;
    showAvailability?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showCartCount?: boolean;
    showGroups?: boolean;
    readOnly?: boolean;
    buyAlwaysAdd?: boolean;
    resetSelectedQuantities?: Set<number>;
    onResetSelectedQuantities?: () => void;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    validatedProductAccess?: ValidateProductAccessResult[];
    actionsAvailable?: IActionsAvailable;
    cartViewProps?: ICartViewProps | undefined;
    wishlistItemsProps?: IWishlistItemsProps<IWishlistItemsData> | undefined;
    orderTemplateViewProps?: IOrderTemplateViewProps;
    selectOnQuantityChange?: boolean;
    breakoffProducts?: { [key: number]: boolean };
    selectedProducts?: IProductSelection;
    wishlists?: CommerceList[];
    onUpdateQuantity?: (product: ProductListProduct, quantity: number) => void;
    onUpdateSelection?: (selection: IProductSelection) => void;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (productRecId: number | undefined, targetRecId: number | undefined, direction: 'over' | 'under') => void;
    onMove?: (recordId: number | undefined, direction: 'up' | 'down') => void;
    onBuyValidate?: (product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>;
}

export interface IProductSelection {
    isAllSelected: boolean;
    isDisabled: boolean;
    [key: string]: boolean;
}

export interface BuyValidationResult {
    isValid: boolean;
    message?: string;
}

export const ProductListComponent: React.FC<IProductListItems> = ({
    type = 'default',
    cart,
    products,
    simpleProducts,
    unitConversions,
    productErrors,
    orderTemplate,
    customerInformation,
    context,
    config,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    allowMultiSelect,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isLoading,
    isPriceMinMaxEnabled,
    readOnly,
    disableBuyButtons,
    disableReorder,
    disableQuantityInput,
    draggable,
    showAvailability,
    showPrices,
    showTotalPrices,
    showCartCount,
    showGroups,
    buyAlwaysAdd,
    resetSelectedQuantities,
    onResetSelectedQuantities,
    validatedProductAccess,
    productsDimensionAvailabilities,
    actionsAvailable,
    cartViewProps,
    wishlistItemsProps,
    orderTemplateViewProps,
    selectOnQuantityChange,
    breakoffProducts,
    selectedProducts: selectedProductsProp,
    wishlists,
    onUpdateQuantity,
    onUpdateSelection,
    onBreakoffChange,
    onDrop,
    onMove,
    onBuyValidate
}) => {
    const [selectedProducts, setSelectedProducts] = useState<IProductSelection>(selectedProductsProp || { isAllSelected: false, isDisabled: true });
    const [orderTemplates, setOrderTemplates] = useState<OrderTemplate[] | undefined>(undefined);
    const [dragOverProduct, setDragOverProduct] = useState<number | undefined>(undefined);
    const [dragOverUnder, setDragOverUnder] = useState<'over' | 'under' | undefined>(undefined);
    const cartQueue = useMemo(() => new PQueue({ concurrency: 1 }), []);

    useEffect(() => {
        setSelectedProducts(selectedProductsProp || { isAllSelected: false, isDisabled: true });
    }, [selectedProductsProp]);

    useEffect(() => {
        onUpdateSelection?.(selectedProducts);
    }, [selectedProducts]);

    React.useEffect(() => {
        document.querySelectorAll('.msc-add-to-order-template-icon').forEach(element => {
            element.setAttribute('title', element.getAttribute('aria-label') || '');
        });

        // Remove selection from products that are no longer available (for example on another page)
        Object.keys(selectedProducts).forEach(key => {
            const id = Number(key);

            // Will also skip isAllSelected and isDisabled
            if (isNaN(id)) {
                return;
            }

            if (products.every(x => x.RecordId !== id)) {
                delete selectedProducts[key];
            }
        });
    }, [products]);

    // Setup navigation with Enter and Shift+Enter for quantity inputs
    React.useEffect(() => {
        const inputs = document.querySelectorAll('.dlvry-quantity__quantity-input');
        const keyDownHandler = (e: Event) => {
            const event = e as KeyboardEvent;
            const index = Number((event.currentTarget as HTMLElement).getAttribute('data-index'));

            if (event.key === 'Enter') {
                if (event.shiftKey) {
                    if (index === 0) {
                        return;
                    }
                    (inputs.item(index - 1) as HTMLInputElement).select();
                } else {
                    if (index === inputs.length - 1) {
                        return;
                    }
                    (inputs.item(index + 1) as HTMLInputElement).select();
                }
            }
        };

        inputs.forEach((element, index) => {
            element.setAttribute('data-index', index.toString());
            element.addEventListener('keydown', keyDownHandler);
        });

        return () => {
            inputs.forEach(element => {
                element.removeEventListener('keydown', keyDownHandler);
            });
        };
    }, [products]);

    React.useEffect(() => {
        if (resetSelectedQuantities) {
            resetSelectedQuantities.forEach(id => {
                if (products.find(x => x.RecordId === id)) {
                    products.find(x => x.RecordId === id)!.CurrentQuantity = 0;
                }
            });

            onResetSelectedQuantities?.();
        }
    }, [resetSelectedQuantities]);

    const changeSelection = (id: number | undefined) => {
        if (id) {
            setSelectedProducts((prevState: any) => {
                prevState[id] = !prevState[id];
                return { ...prevState };
            });
        }
        checkIsAllSelect();
    };
    const checkIsAllSelect = () => {
        const selectedCartItem = products.filter(product => {
            return product.RecordId && selectedProducts[product.RecordId];
        });
        if (selectedCartItem.length === products.length) {
            setSelectedProducts((prv: any) => {
                return { ...prv, isAllSelected: true, isDisabled: false };
            });
        } else {
            setSelectedProducts((prv: any) => {
                return { ...prv, isAllSelected: false, isDisabled: selectedCartItem.length === 0 };
            });
        }
    };
    // @ts-ignore
    const selectAll = () => {
        const allProductsSelection = { isAllSelected: !selectedProducts['isAllSelected'], isDisabled: selectedProducts['isAllSelected'] };
        products.forEach(product => {
            if (product.RecordId) {
                allProductsSelection[product.RecordId] = !selectedProducts['isAllSelected'];
            }
        });
        setSelectedProducts(allProductsSelection);
    };
    const emptyCart = async () => {
        const cart = await getCartState(context.actionContext);
        await cart.removeCartLines({ cartLineIds: cart.cart.CartLines!.map(x => x.LineId!) });
    };
    const removeAllSelectedCarts = async () => {
        if (cartViewProps && cartViewProps.cartlines) {
            try {
                const cartState = await getCartState(context.actionContext);
                const lineItemIdsToRemove: any[] = [];
                cartViewProps.cartlines.forEach(cartLine => {
                    if (cartLine?.data?.product?.RecordId && selectedProducts[cartLine?.data?.product?.RecordId]) {
                        delete selectedProducts[cartLine?.data?.product?.RecordId];
                        lineItemIdsToRemove.push(cartLine.data.cartline.LineId);
                    }
                });
                if (lineItemIdsToRemove.length > 0) {
                    await cartState.removeCartLines({ cartLineIds: lineItemIdsToRemove });
                    checkIsAllSelect();
                }
            } catch (error) {
                throw error;
            }
        }
    };
    const updateOrderTemplatesAsync = async (newOrderTemplate?: ProductList) => {
        let newOrderTemplates;

        if (newOrderTemplate) {
            newOrderTemplates = [...(orderTemplates ? orderTemplates : [])];

            // Add new order template if it doesn't exist
            if (newOrderTemplates.every(x => x.productList.Id !== newOrderTemplate.Id)) {
                newOrderTemplates.splice(0, 0, new OrderTemplate(newOrderTemplate, [], 0));
            }
        } else {
            // Refresh order templates incase new order template was created
            const queryResultSettings = QueryResultSettingsProxy.fromInputData({
                requestContext: context.request,
                config: config
            }).QueryResultSettings;
            const input = new GetOrderTemplatesInput('', queryResultSettings);
            newOrderTemplates = (await getOrderTemplates(input, context.actionContext)).map(x => new OrderTemplate(x, [], 0));
        }

        setOrderTemplates(newOrderTemplates);
        return newOrderTemplates;
    };
    const handleDrop = async (e: React.DragEvent<HTMLElement>, recordId: number | undefined, overUnder: "over" | "under" | undefined) => {
        const dragData = JSON.parse(e.dataTransfer.getData('product'));
        const dragProduct = products.find(product => product.RecordId === dragData.ProductRecordId);
        const targetProduct = products.find(product => product.RecordId === recordId);

        if (!dragProduct || !targetProduct || !overUnder) {
            return;
        }

        onDrop?.(dragProduct?.RecordId, targetProduct?.RecordId, overUnder);
    };

    const grouped = products.reduce((acc, product) => {
        const key = showGroups && product.RequestedDeliveryDate ? new Date(product.RequestedDeliveryDate).getTime().toString() : 'default';
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(product);
        return acc;
    }, {} as { [key: string]: ProductListProduct[] });
    const createdGroupFor: string[] = [];

    return (
        <div>
            {moduleType === 'dlvry-cart' && (
                <div className='ms-cart__list-buttons'>
                    <button className='ms-cart__list-buttons__empty-cart' onClick={emptyCart}>
                        {resources.emptyCartButtonText}
                    </button>

                    <button
                        className={`ms-cart__list-buttons__remove ${selectedProducts['isDisabled'] ? 'disabled' : ''}`}
                        onClick={removeAllSelectedCarts}
                    >
                        {resources.removeSelectedToCartText}
                    </button>
                </div>
            )}

            <ul
                className={classnames('product-list-component list product-listing')}
                onDrop={async e => {
                    await handleDrop(e, dragOverProduct, 'under');
                }}
                onDragOver={e => {
                    e.stopPropagation();

                    if (!disableReorder && e.dataTransfer.types.includes('product')) {
                        e.preventDefault();

                        const firstItem = document.querySelector('.product-list-component > li:first-of-type');
                        const margin = firstItem ? parseFloat(window.getComputedStyle(firstItem).marginBottom) + 6 : 22;
                        const item = document.elementFromPoint(e.clientX, e.clientY - margin);

                        let listItem = item?.parentElement;
                        while (listItem) {
                            if (listItem.tagName === 'LI') {
                                break;
                            }
                            listItem = listItem.parentElement;
                        }

                        if (!listItem) {
                            return;
                        }

                        const productId = listItem?.getAttribute('data-product-id') ? parseFloat(listItem.getAttribute('data-product-id')!) : undefined;

                        setDragOverProduct(productId);
                        setDragOverUnder('under');
                    }
                }}
            >
                {/* <ProductListHeader
                    className='product-search'
                    context={context}
                    id='product-search-table'
                    typeName='product-search-table'
                    data={{}}
                    resources={resources}
                    actionsAvailable={actionsAvailable}
                    moduleType={moduleType}
                    isSelected={selectedProducts['isAllSelected']}
                    onSelectCartHandler={selectAll}
                /> */}
                {Object.keys(grouped).map((key, index) => {
                    const items: React.ReactNode[] = [];

                    if (key !== 'default' && !createdGroupFor.includes(key)) {
                        items.push(
                            <li key={`${key}-group`} className='product-list-group-header'>
                                {resources.deliveryDateLabel}: {context.cultureFormatter.formatDate(new Date(parseFloat(key)), { day: 'numeric', month: 'long' })}
                            </li>
                        );
                        createdGroupFor.push(key);
                    }

                    for (const product of grouped[key]) {
                        items.push((
                            <ProductListRow
                                key={`${product.RecordId}-${product.DefaultUnitOfMeasure}`}
                                context={context}
                                typeName='product-search-table-row'
                                id={`product-search-table-row-${index}`}
                                className='product-search-table-row'
                                resources={resources}
                                cartQueue={cartQueue}
                                selectOnQuantityChange={selectOnQuantityChange}
                                isDragOver={!disableReorder && product.RecordId === dragOverProduct ? dragOverUnder : undefined}
                                isSelected={product.RecordId ? selectedProducts[product.RecordId] : false}
                                onSelectCartHandler={() => changeSelection(product.RecordId)}
                                data={{
                                    cart,
                                    product,
                                    simpleProduct: simpleProducts.find(simpleProduct => simpleProduct.RecordId === product.RecordId),
                                    orderTemplate,
                                    customerInformation,
                                    unitConversions: unitConversions?.filter(x => x.ProductRecordId === product.RecordId),
                                    wishlists
                                }}
                                productComparisonButton={productComparisonButton}
                                actionsAvailable={actionsAvailable}
                                cartViewProps={cartViewProps}
                                wishlistItemsProps={wishlistItemsProps}
                                moduleId={moduleId}
                                moduleType={moduleType}
                                error={productErrors?.find(x => x.RecordId === product.RecordId)?.Error}
                                allowMultiSelect={allowMultiSelect}
                                min={type === 'cart' ? 1 : undefined}
                                readOnly={readOnly}
                                disableBuyButton={disableBuyButtons}
                                disableReorder={disableReorder}
                                disableQuantityInput={disableQuantityInput}
                                draggable={draggable}
                                lazyLoadImages={imageSettings?.lazyload}
                                showAvailability={showAvailability}
                                showPrices={showPrices}
                                showTotalPrices={showTotalPrices}
                                showCartCount={showCartCount}
                                buyAlwaysAdd={buyAlwaysAdd}
                                resetSelectedQuantities={!!product.RecordId && resetSelectedQuantities?.has(product.RecordId)}
                                initialBreakoffEnabled={Boolean(product.RecordId && breakoffProducts?.[product.RecordId])}
                                userHasAccess={validatedProductAccess?.find(x => x.RecordId === product.RecordId)?.HasAccess}
                                onUpdateOrderTemplates={updateOrderTemplatesAsync}
                                orderTemplateViewProps={orderTemplateViewProps}
                                retrieveOrderTemplates={async () => {
                                    return orderTemplates || updateOrderTemplatesAsync();
                                }}
                                onUpdateQuantity={quantity => {
                                    onUpdateQuantity?.(product, quantity);
                                }}
                                onBreakoffChange={onBreakoffChange}
                                onDrop={handleDrop}
                                onDragOver={(recordId, overUnder) => {
                                    setDragOverProduct(recordId);
                                    setDragOverUnder(overUnder);
                                }}
                                onMove={onMove}
                                onBuyValidate={onBuyValidate}
                            />
                        ));
                    }

                    return items;
                })}
            </ul>

            {isLoading && (
                <div className='product-search-loading-spinner-wrapper'>
                    <div className='product-search-loading-spinner'></div>
                </div>
            )}
        </div>
    );
};

const getInventoryLabel = (
    channelInventoryConfigurationId: number | undefined,
    attributeValues: AttributeValue[] | undefined,
    recordId: number,
    productInventoryStatus?: any
): string | undefined => {
    if (productInventoryStatus) {
        const shippingInventory = productInventoryStatus.find((shipping: any) => shipping.ProductAvailableQuantity.ProductId === recordId);
        if (shippingInventory && shippingInventory.StockLevelLabel) {
            return shippingInventory.StockLevelLabel;
        }
    }
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(attributeValues)) {
        return undefined;
    }
    const inventoryAttribute = attributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
};

export const productSearchResults2ProductList = (
    context: ICoreContext,
    searchResults: ProductSearchResultExt[],
    channelInventoryConfigurationId: number | undefined,
    cart?: ICartState,
    productInventoryStatus?: IProductInventoryInformation[]
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];
    const attributeLocale = getLocale(context);

    if (!searchResults) {
        return productList;
    }

    searchResults.map((searchResult, index) => {
        const cartLine = cart?.cart.CartLines?.find(x => x.ProductId === searchResult.RecordId);
        const productAvailability = productInventoryStatus?.find(x => x.ProductAvailableQuantity.ProductId === searchResult.RecordId);

        productList.push(
            new ProductListProduct(
                searchResult.RecordId,
                searchResult.ItemId,
                searchResult.Name,
                searchResult.PrimaryImageUrl,
                searchResult.DefaultUnitOfMeasure,
                searchResult.Price,
                getInventoryLabel(
                    channelInventoryConfigurationId,
                    searchResult.AttributeValues,
                    searchResult.RecordId,
                    productInventoryStatus
                ),
                cartLine?.Quantity,
                cartLine,
                undefined,
                undefined,
                productAvailability,
                searchResult.AttributeValues?.find(x => x.Name === attributeLocale.brand)?.TextValue,
                searchResult.AttributeValues?.find(x => x.Name === attributeLocale.manufacturer)?.TextValue,
                searchResult.PriceFailed,
                undefined,
                undefined,
                searchResult.Badge,
                searchResult.ProductPrice,
                searchResult.AttributeValues?.find(x => x.Name === attributeLocale.transit)?.TextValue === '1',
                getTemperatureZone(context.request.locale, searchResult.AttributeValues)
            )
        );

        productList[productList.length - 1].BasePrice = searchResult.BasePrice;
    });

    return productList;
};

export const fullProducts2ProductList = (
    fullProducts: FullProduct[] | undefined,
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!fullProducts) {
        return productList;
    }

    fullProducts.map((fullProduct, index) => {
        let brand;
        let manufacturer;
        let transit;
        let temperatureZone;

        if ((fullProduct.ProductDetails as SimpleProductExt).Brand) {
            brand = (fullProduct.ProductDetails as SimpleProductExt).Brand;
        }

        if ((fullProduct.ProductDetails as SimpleProductExt).Manufacturer) {
            manufacturer = (fullProduct.ProductDetails as SimpleProductExt).Manufacturer;
        }

        if ((fullProduct.ProductDetails as SimpleProductExt).Transit) {
            transit = (fullProduct.ProductDetails as SimpleProductExt).Transit;
        }

        if ((fullProduct.ProductDetails as SimpleProductExt).TemperatureZone) {
            temperatureZone = (fullProduct.ProductDetails as SimpleProductExt).TemperatureZone;
        }

        productList.push(
            new ProductListProduct(
                fullProduct.ProductDetails.RecordId,
                fullProduct.ProductDetails.ItemId,
                fullProduct.ProductDetails.Name,
                fullProduct.ProductDetails.PrimaryImageUrl,
                fullProduct.ProductDetails.DefaultUnitOfMeasure,
                fullProduct.ProductDetails.Price,
                productAvailabilities?.find(
                    avail => avail.ProductAvailableQuantity.ProductId === fullProduct.ProductDetails.RecordId
                )?.StockLevelLabel,
                undefined,
                undefined,
                undefined,
                undefined,
                productAvailabilities?.find(avail => avail.ProductAvailableQuantity.ProductId === fullProduct.ProductDetails.RecordId),
                brand,
                manufacturer,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                transit,
                temperatureZone
            )
        );
    });

    return productList;
};

export const cartLines2ProductList = (
    cartLines: ICartlinesViewProps[],
    productAvailabilities: IProductInventoryInformation[] | undefined,
    context: ICoreContext,
    activePrices: ProductPrice[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!cartLines) {
        return productList;
    }

    cartLines.map((cartLine, index) => {
        const product = cartLine.data?.product as SimpleProductExt | undefined;
        /* const price = cartLine.data?.cartline.Price || 0; */
        const price = cartLine.data?.cartline?.NetAmountWithoutTax ?
            cartLine.data?.cartline.NetAmountWithoutTax / (cartLine.data?.cartline?.Quantity || 1) :
            cartLine.data?.cartline.Price || 0;
        const fallImageUrl = getFallbackImageUrl(product?.ItemId, context.request.apiSettings);
        const productPrice = activePrices?.find(x => x.ProductId === product?.RecordId);
        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === product?.RecordId
        );

        productList.push(
            new ProductListProduct(
                product?.RecordId,
                product?.ItemId,
                product?.Name,
                product?.PrimaryImageUrl || fallImageUrl,
                product?.DefaultUnitOfMeasure,
                price,
                availability?.StockLevelLabel,
                cartLine.data?.cartline.Quantity,
                cartLine.data?.cartline,
                cartLine.data?.cartline.DiscountAmount,
                cartLine.data?.cartline.NetAmountWithoutTax,
                availability,
                product?.Brand,
                product?.Manufacturer,
                undefined,
                undefined,
                product?.CategoryPaths,
                getDiscountBadge(productPrice),
                productPrice,
                product?.Transit,
                product?.TemperatureZone
            )
        );
    });

    return productList;
};

export const orderTemplateLines2ProductList = (
    lines: CountingListLine[],
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!lines) {
        return productList;
    }

    lines.map((line, index) => {
        let brand: string | undefined;
        let manufacturer: string | undefined;
        let categoryPaths: CategoryPath[] | undefined;
        let badge: string | undefined;
        let productPrice: ProductPrice | undefined;
        let transit: boolean | undefined;
        let temperatureZone: string | undefined;

        if ((line.fullProduct?.ProductDetails as SimpleProductExt)?.Brand) {
            brand = (line.fullProduct?.ProductDetails as SimpleProductExt).Brand;
        }

        if ((line.fullProduct?.ProductDetails as SimpleProductExt)?.Manufacturer) {
            manufacturer = (line.fullProduct?.ProductDetails as SimpleProductExt).Manufacturer;
        }

        if ((line.fullProduct?.ProductDetails as SimpleProductExt)?.CategoryPaths) {
            categoryPaths = (line.fullProduct?.ProductDetails as SimpleProductExt).CategoryPaths;
        }

        if ((line.fullProduct?.ProductDetails as SimpleProductExt)?.Badge) {
            badge = (line.fullProduct?.ProductDetails as SimpleProductExt).Badge;
        }

        if ((line.fullProduct?.ProductDetails as SimpleProductExt)?.ProductPrice) {
            productPrice = (line.fullProduct?.ProductDetails as SimpleProductExt).ProductPrice;
        }

        if ((line.fullProduct?.ProductDetails as SimpleProductExt)?.Transit) {
            transit = (line.fullProduct?.ProductDetails as SimpleProductExt).Transit;
        }

        if ((line.fullProduct?.ProductDetails as SimpleProductExt)?.TemperatureZone) {
            temperatureZone = (line.fullProduct?.ProductDetails as SimpleProductExt).TemperatureZone;
        }

        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === line.fullProduct?.ProductDetails.RecordId
        );

        const displayOrder = line.productListLine.ExtensionProperties?.find(prop => prop.Key === 'DISPLAYORDER')?.Value?.DecimalValue;

        const product = new ProductListProduct(
            line.fullProduct?.ProductDetails.RecordId,
            line.fullProduct?.ProductDetails.ItemId,
            line.fullProduct?.ProductDetails.Name,
            line.fullProduct?.ProductDetails.PrimaryImageUrl,
            line.productListLine.UnitOfMeasure,
            line.fullProduct?.ProductDetails.Price,
            availability?.StockLevelLabel,
            undefined,
            undefined,
            undefined,
            undefined,
            availability,
            brand,
            manufacturer,
            undefined,
            displayOrder,
            categoryPaths,
            badge,
            productPrice,
            transit,
            temperatureZone
        );

        product.CurrentQuantity = line.productListLine.Quantity;
        product.LineCreatedDateTime = line.productListLine.CreatedDateTime || line.productListLine.LastUpdated;

        if (typeof product.LineCreatedDateTime === 'string') {
            product.LineCreatedDateTime = new Date(product.LineCreatedDateTime);
        }

        product.BasePrice = line.fullProduct?.ProductDetails.BasePrice;
        product.PriceFailed = (line as any).PriceFailed;

        productList.push(product);
    });

    return productList;
};
